import React, { useState } from 'react';
import { FaCalculator, FaFileInvoiceDollar, FaChartPie } from 'react-icons/fa';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SetBudgetImage from '../images/1.Set budget.gif';
import AddExpenseImage from '../images/2.Add expense.gif';
import ChangeExpenseImage from '../images/3.Change expense.gif';
import ChangeBudgetImage from '../images/4.change budget.gif';

const Feature = ({ icon, title, description }) => (
  <div className="flex flex-col items-center text-center p-10">
    {icon}
    <h3 className="mt-8 text-3xl font-semibold">{title}</h3>
    <p className="mt-6 text-xl">{description}</p>
  </div>
);
Feature.propTypes = {
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
};

const BudgetSection = ({ image, title, description, reverse }) => (
  <div className={`flex flex-col md:flex-row ${reverse ? 'md:flex-row-reverse' : ''} items-center mb-40`}>
    <img src={image} alt={title} className="w-full md:w-1/2 h-auto mb-12 md:mb-0 md:mx-20" />
    <div className="w-full md:w-1/2 px-12">
      <h2 className="text-4xl font-bold mb-8">{title}</h2>
      <p className="text-2xl">{description}</p>
    </div>
  </div>
);
BudgetSection.propTypes = {
  image: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  reverse: PropTypes.bool,
};

export default function BudgetTrackerpage() {
  const [openQuestion, setOpenQuestion] = useState(null);
  const { t } = useTranslation();
  const navigate = useNavigate();

  const toggleQuestion = (index) => {
    setOpenQuestion(openQuestion === index ? null : index);
  };

  return (
    <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100">
      {/* Top section */}
      <div className="w-full max-w-8xl mx-auto px-12 py-24">
        <h1 className="text-6xl font-bold text-center mb-12">{t('Say goodbye to budget stress')}</h1>
        <p className="text-3xl text-center mb-24">
          {t("Don't worry about going over budget customize the numbers, track your spending, and schedule payments")}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 mb-32">
          <Feature
            icon={<FaCalculator className="text-7xl text-blue-500" />}
            title={t('Let us do the math')}
            description={t("Enter your budget and we'll estimate category costs for you.")}
          />
          <Feature
            icon={<FaFileInvoiceDollar className="text-7xl text-gray-500" />}
            title={t('Customize as needed')}
            description={t("Adjust your budget and create new categories as needed.")}
          />
          <Feature
            icon={<FaChartPie className="text-7xl text-teal-500" />}
            title={t('Run the numbers')}
            description={t("Monitor your costs and view a breakdown of your wedding spend.")}
          />
        </div>

        <div className="text-center mb-32">
          <h2 className="text-5xl font-bold mb-10">{t('Stay on track')}</h2>
          <p className="text-3xl">
            {t('Track your spending as you go and manage vendor payments.')}
          </p>
        </div>
      </div>

      {/* Budget sections */}
      <div className="w-full max-w-8xl mx-auto px-12 py-24">
        <BudgetSection
          image={SetBudgetImage}
          title={t('Set Budget')}
          description={t('Set a money plan that fits your dream wedding. Our budget tool helps you make a money plan that works for you and your partner.')}
        />
        <BudgetSection
          image={AddExpenseImage}
          title={t('Add Expense')}
          description={t('As you spend money, add it to your budget. This keeps you up-to-date on how much you\'ve spent, so you can stay on track.')}
          reverse
        />
        <BudgetSection
          image={ChangeExpenseImage}
          title={t('Change Expense')}
          description={t('Oops, made a mistake? No problem! You can easily fix any expenses you entered wrong. This keeps your budget accurate.')}
        />
        <BudgetSection
          image={ChangeBudgetImage}
          title={t('Change Budget')}
          description={t('If your plans change or new costs come up, you can adjust your overall budget. This flexibility lets you have the wedding you want without going over your limit.')}
          reverse
        />
      </div>

      {/* FAQ section */}
      <div className="w-full max-w-4xl mx-auto space-y-16 px-12 py-24">
        <button
          className="w-full flex justify-center py-4 px-8 border border-transparent rounded-md shadow-sm text-xl font-medium text-white bg-pink-600 hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-pink-500"
          onClick={() => navigate('/dashboard/budget-tracker')}
        >
          {t('Go to Budget Tracker')}
        </button>
        <div className="mt-16 text-lg text-gray-600">
          <h2 className="text-3xl font-bold mb-8">{t('Frequently Asked Questions')}</h2>
          <p className="mb-10">{t('Find answers to your budget questions here.')}</p>
          <dl className="space-y-10">
            {[
              { q: t('Is your Wedding Budget planner free?'), a: t('Yes, the Hayya Budget Planner Tool is 100% free.') },
              { q: t('What is a reasonable budget for a wedding?'), a: t('Your wedding budget with vary significantly based on your location, guest count and your taste. Take a look at the Hayya blogs to get an understanding of typical spend for each category and region.') },
              { q: t('How many people go over budget for a wedding?'), a: t('Nearly 60% of couples increase their initial budget. While this is common, using our free Wedding Planning Tools can help you stay on track and realistically understand your budget.') },
            ].map((item, index) => (
              <div key={index} className="border-b border-gray-200 pb-6">
                <dt
                  className="text-2xl font-bold cursor-pointer flex justify-between items-center"
                  onClick={() => toggleQuestion(index)}
                >
                  {item.q}
                  <span>{openQuestion === index ? '▲' : '▼'}</span>
                </dt>
                {openQuestion === index && <dd className="mt-6 text-xl">{item.a}</dd>}
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
